import React, { useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useAudio } from '../context/AudioContext';

const MusicPlayer = () => {
  const {
    audioRef,
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    currentTrackIndex,
    setCurrentTrackIndex,
    volume,
    setVolume,
    playlist,
    currentTrack
  } = useAudio();

  useEffect(() => {
    const audio = audioRef.current;
    
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      // Auto-play next track when current one ends
      playNext();
    };
    
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrackIndex]);

  // Load new track when index changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.load();
      audio.volume = volume; // Set volume
      if (isPlaying) {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.error('Playback failed:', error);
            setIsPlaying(false);
          });
        }
      }
    }
  }, [currentTrackIndex]);

  // Update volume when it changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error('Playback failed:', error);
            setIsPlaying(false);
          });
      }
    }
  };

  const playNext = () => {
    setCurrentTrackIndex((prevIndex) => (prevIndex + 1) % playlist.length);
  };

  const playPrevious = () => {
    if (currentTime > 3) {
      // If more than 3 seconds into the track, restart it
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    } else {
      // Otherwise, go to previous track
      setCurrentTrackIndex((prevIndex) => 
        prevIndex === 0 ? playlist.length - 1 : prevIndex - 1
      );
    }
  };

  const handleSeek = (e) => {
    const seekBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const seekBarWidth = seekBar.offsetWidth;
    const seekTime = (clickPosition / seekBarWidth) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (e) => {
    const volumeBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const volumeBarWidth = volumeBar.offsetWidth;
    const newVolume = clickPosition / volumeBarWidth;
    setVolume(Math.max(0, Math.min(1, newVolume))); // Clamp between 0 and 1
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const seekPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {/* Music Player - Redesigned with large spinning disc on left */}
      <div className="w-[200px] h-[80px] bg-gradient-to-r from-black/60 to-black/40 backdrop-blur-md rounded-2xl shadow-2xl overflow-visible flex items-center relative">
        {/* Large Spinning Album Art - Left Corner */}
        <div className="absolute -left-2 top-1/2 -translate-y-1/2 z-10">
          <div className={`relative w-20 h-20 rounded-full overflow-hidden shadow-2xl border-4 border-white/20 ${isPlaying ? 'animate-spin-slow' : ''}`}>
            <img
              src={currentTrack.artwork}
              alt={currentTrack.album}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(45,100%,70%)]/10 to-transparent" />
            
            {/* Center dot (like a vinyl record) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-black/60 rounded-full border border-white/30" />
          </div>
        </div>

        {/* Content Area - Shifted right to make room for disc */}
        <div className="flex-1 ml-20 pr-3 py-2 flex flex-col justify-between h-full">
          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-[10px] font-bold text-white truncate leading-tight">
              {currentTrack.name}
            </h3>
            <p className="text-[8px] text-gray-300 truncate font-medium">
              {currentTrack.album}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mt-1">
            <div 
              className="relative h-1 bg-white/20 rounded-full cursor-pointer group"
              onClick={handleSeek}
            >
              <div 
                className="absolute h-full bg-[hsl(45,100%,70%)] rounded-full transition-all duration-200"
                style={{ width: `${seekPercentage}%` }}
              />
              {/* Hover indicator */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute h-full bg-white/10 rounded-full" />
              </div>
            </div>

            {/* Time Display */}
            <div className="flex justify-between text-[7px] text-gray-400 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-2 mt-1">
            <button
              className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 group"
              onClick={playPrevious}
              title="Previous track"
            >
              <SkipBack className="w-3 h-3 text-white group-hover:text-[hsl(45,100%,70%)]" />
            </button>

            <button
              className="w-8 h-8 rounded-full bg-[hsl(45,100%,70%)] hover:bg-[hsl(45,100%,60%)] flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg active:scale-95"
              onClick={togglePlay}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-black" fill="black" />
              ) : (
                <Play className="w-4 h-4 text-black ml-0.5" fill="black" />
              )}
            </button>

            <button
              className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 group"
              onClick={playNext}
              title="Next track"
            >
              <SkipForward className="w-3 h-3 text-white group-hover:text-[hsl(45,100%,70%)]" />
            </button>
          </div>

          {/* Volume Control */}
          <div className="mt-1 flex items-center gap-1">
            <span className="text-[7px] text-gray-400">Vol</span>
            <div 
              className="flex-1 h-1 bg-white/20 rounded-full cursor-pointer group relative"
              onClick={handleVolumeChange}
              title={`Volume: ${Math.round(volume * 100)}%`}
            >
              <div 
                className="absolute h-full bg-[hsl(45,100%,70%)] rounded-full transition-all duration-200"
                style={{ width: `${volume * 100}%` }}
              />
              {/* Hover indicator */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute h-full bg-white/10 rounded-full" />
              </div>
            </div>
            <span className="text-[7px] text-gray-400 w-6 text-right">{Math.round(volume * 100)}%</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .blur-\[2px\] {
          filter: blur(2px);
        }
      `}</style>
    </>
  );
};

export default MusicPlayer;
